import React from 'react';
import { t } from '@lingui/macro';

import { Box, Button, Flex, Text } from '@rover/kibble/core';
import Avatar from '@rover/kibble/patterns/Avatar';
import Divider from '@rover/kibble/patterns/Divider';
import StarRating from '@rover/kibble/patterns/StarRating';

import type { NameSearchResult } from '../types';

type Props = {
  result: NameSearchResult;
};

const getDisplayName = (fullName: string): string => {
  const parts = fullName.trim().split(/\s+/);
  if (parts.length <= 1) return fullName;
  const firstName = parts[0];
  const lastInitial = parts[parts.length - 1]?.[0] ?? '';
  return `${firstName} ${lastInitial}.`;
};

const SearchByNameSitterCard = ({ result }: Props): JSX.Element => {
  const { personName, avatarUrl, ratingsAverage, reviewsCount, tagline, profileUrl } = result;
  const displayName = getDisplayName(personName);
  const ratingValue = parseFloat(ratingsAverage);
  const safeRating = Number.isFinite(ratingValue) ? ratingValue : 0;

  return (
    <Box testID={`search-by-name-sitter-card-${result.personOpk}`}>
      <Box py="4x">
        <Flex gap="3x" alignItems="flex-start">
          <Avatar imageUrl={avatarUrl} type="person" size="default" alt="" shape="circle" />
          <Box flex="1">
            <Flex justifyContent="space-between" alignItems="center" mb="1x">
              <Text size="200" fontWeight="semibold" textColor="primary" as="h4">
                {displayName}
              </Text>
              {reviewsCount > 0 && (
                <Flex
                  alignItems="center"
                  gap="1x"
                  webProps={{
                    'aria-label': t`Rated ${ratingsAverage} out of 5 from ${reviewsCount} reviews`,
                  }}
                >
                  <StarRating
                    rating={safeRating}
                    variant="compact"
                    textSize="100"
                    fontWeight="semibold"
                  />
                  <Text size="100" textColor="secondary">
                    ({reviewsCount})
                  </Text>
                </Flex>
              )}
            </Flex>
            <Text size="100" textColor="secondary">
              {tagline}
            </Text>
            <Flex gap="2x" mt="4x">
              <Button
                variant="flat"
                size="small"
                href={profileUrl}
                webProps={{ 'aria-label': t`View Profile, ${displayName}` }}
                testID={`search-by-name-view-profile-${result.personOpk}`}
              >
                {t`View Profile`}
              </Button>
              <Button
                variant="primary"
                size="small"
                href={`${profileUrl}contact/`}
                webProps={{ 'aria-label': t`Send Intro Message to ${displayName}` }}
                testID={`search-by-name-send-message-${result.personOpk}`}
              >
                {t`Send Intro Message`}
              </Button>
            </Flex>
          </Box>
        </Flex>
      </Box>
      <Divider />
    </Box>
  );
};

export default SearchByNameSitterCard;
